import Item from 'src/models/item/Item'
import ItemVariation from 'src/models/item/ItemVariation'
import Model, { encoders, ID } from 'src/models/Model'
import ItemOrderItemModifier from 'src/models/order-item/ItemOrderItemModifier'
import User from 'src/models/user/User'

export default class ItemOrderItem extends Model {
  static name = 'ItemOrderItem'

  static versioned = true

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      item: {
        type: Item,
        output: true,
        reference: true
      },
      itemVariation: {
        type: ItemVariation,
        encode: encoders.hasOne,
        reference: true
      },
      itemModifiers: {
        type: [ItemOrderItemModifier],
        required: true
      },
      itemModifierNames: {
        type: [String],
        output: true,
        excludeFromDefault: true
      },
      user: {
        type: User,
        encode: encoders.hasOne,
        reference: true
      },
      price: {
        type: Number,
        output: true,
        computed () {
          let price = 0
          if (this.itemVariation) {
            price += this.itemVariation.price || 0
          }
          _.each(this.itemModifiers?.data, (itemModifier) => {
            price += itemModifier?.modifier?.price || 0
          })
          return price
        }
      },
      adminApproved: {
        type: Boolean,
        default: false,
        local: true
      }
    }
  }

  itemVariationSelected (itemVariation) {
    return this.itemVariation && this.itemVariation.id === itemVariation.id
  }

  toggleItemVariation (itemVariation) {
    this.itemVariationId = itemVariation.id
  }

  hasModifier (modifier) {
    return _.some(this.itemModifiers?.data, (itemModifier) => itemModifier.modifierId === modifier.id)
  }

  toggleModifier (modifier) {
    if (this.hasModifier(modifier)) {
      this.removeModifier(modifier)
    } else {
      this.addModifier(modifier)
    }
  }

  modifiersSelected (modifierSet) {
    const setModifierIds = _.map(modifierSet.modifiers.data, 'id')
    const itemModifierIds = _.map(this.itemModifiers.data, 'modifier.id')
    return _.intersection(setModifierIds, itemModifierIds).length
  }

  clearAllModifiersForModifierSet (modifierSet) {
    const setModifierIds = _.map(modifierSet.modifiers.data, 'id')
    const itemModifierIds = _.map(this.itemModifiers.data, 'modifier.id')
    const toRemoveIds = _.intersection(setModifierIds, itemModifierIds)
    _.each(toRemoveIds, (modifierId) => {
      this.removeModifier({ id: modifierId })
    })
  }

  itemModifierSetForModifier (modifier) {
    return _.find(this.item.itemModifierSets.data, (itemModifierSet) => itemModifierSet.modifierSetId === modifier.modifierSetId)
  }

  addModifier (modifier) {
    if (this.hasModifier(modifier)) {
      return
    }
    const maxCount = this.itemModifierSetForModifier(modifier).finalMaxSelectionCount
    if (this.modifiersSelected(modifier.modifierSet) >= maxCount) {
      if (maxCount !== 1) {
        return
      }

      // Special behavior when only one selection allowed. Always clear other selection and add this one.
      this.clearAllModifiersForModifierSet(modifier.modifierSet)
    }

    const itemModifier = new ItemOrderItemModifier({ modifierId: modifier.id, price: modifier.price })

    this.itemModifiers.add(itemModifier)
  }

  removeModifier (modifier) {
    // Can't use normal remove because we aren't removing by id, we are removing by modifier id
    this.itemModifiers.filter((model) => model.modifier.id !== modifier.id)
  }

  validateForModifierSet (itemModifierSet) {
    const selectionCount = this.modifiersSelected(itemModifierSet.modifierSet)
    if (itemModifierSet.minSelectionCount !== null && selectionCount < itemModifierSet.minSelectionCount) {
      return `Must select at least ${itemModifierSet.minSelectionCount} option${itemModifierSet.minSelectionCount === 1 ? '' : 's'}`
    }
    if (itemModifierSet.maxSelectionCount !== null && selectionCount > itemModifierSet.maxSelectionCount) {
      return `Can't select more than ${itemModifierSet.minSelectionCount} option${itemModifierSet.minSelectionCount === 1 ? '' : 's'}`
    }
  }

  validate () {
    if (!this.itemVariation || this.itemVariation.isLocal) return false
  }

  afterInflate () {
    if (!this.data.itemId && this.itemVariation) {
      this.data.itemId = this.itemVariation.itemId
    }
  }
}
